import { useCallback, useMemo, useState } from 'react';

import {
  EMPTY_MEMBERS_FILTERS,
  MEMBERS_DEFAULT_PAGE_SIZE,
  MEMBERS_DEFAULT_SORT,
} from '../constants/membersFilters';

/** Filter dimensions that live in the bar chips and the "All filters" slideout. */
const FILTER_KEYS = Object.keys(EMPTY_MEMBERS_FILTERS);

const isActive = (value) => (Array.isArray(value) ? value.length > 0 : Boolean(value));

/**
 * Filter state of the Members list (list-page.mdc, step 2): `search`, the `committed` filters
 * (status, role, group, mfa; single-selects are scalars), the sort and the page.
 * Bar controls write to the committed state and narrow immediately; the slideout edits `draft`,
 * which reaches the committed state on `applyDraft`. Every narrowing resets the page to 1.
 *
 * @param {Partial<typeof EMPTY_MEMBERS_FILTERS>} [initial] Filters applied from the start (e.g. the import batch
 *   a bulk load navigates here with).
 */
export const useMembersFilters = (initial = {}) => {
  const [search, setSearchState] = useState('');
  const [committed, setCommitted] = useState({ ...EMPTY_MEMBERS_FILTERS, ...initial });
  const [draft, setDraft] = useState({ ...EMPTY_MEMBERS_FILTERS, ...initial });
  const [sort, setSortState] = useState(MEMBERS_DEFAULT_SORT);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(MEMBERS_DEFAULT_PAGE_SIZE);

  const setSearch = useCallback((value) => {
    setSearchState(value);
    setPage(1);
  }, []);

  const setFilter = useCallback((key, value) => {
    setCommitted((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  // The quick-filter chips are the same `status` dimension the slideout edits.
  const setStatus = useCallback((value) => setFilter('status', value), [setFilter]);

  const setSort = useCallback((next) => {
    setSortState(next);
    setPage(1);
  }, []);

  const setPageSize = useCallback((size) => {
    setPageSizeState(size);
    setPage(1);
  }, []);

  const openDraft = useCallback(() => setDraft(committed), [committed]);

  const setDraftField = useCallback(
    (key, value) => setDraft((prev) => ({ ...prev, [key]: value })),
    [],
  );

  const applyDraft = useCallback(() => {
    setCommitted(draft);
    setPage(1);
  }, [draft]);

  const clearDraft = useCallback(() => setDraft(EMPTY_MEMBERS_FILTERS), []);

  const clearAll = useCallback(() => {
    setSearchState('');
    setCommitted(EMPTY_MEMBERS_FILTERS);
    setDraft(EMPTY_MEMBERS_FILTERS);
    setPage(1);
  }, []);

  const activeFilterCount = useMemo(
    () => FILTER_KEYS.filter((key) => isActive(committed[key])).length,
    [committed],
  );

  const hasActiveFilters = Boolean(search.trim()) || activeFilterCount > 0;

  return useMemo(
    () => ({
      search,
      ...committed,
      sort,
      page,
      pageSize,
      draft,
      activeFilterCount,
      hasActiveFilters,
      setSearch,
      setStatus,
      setFilter,
      setSort,
      setPage,
      setPageSize,
      openDraft,
      setDraftField,
      applyDraft,
      clearDraft,
      clearAll,
    }),
    [
      search,
      committed,
      sort,
      page,
      pageSize,
      draft,
      activeFilterCount,
      hasActiveFilters,
      setSearch,
      setStatus,
      setFilter,
      setSort,
      setPageSize,
      openDraft,
      setDraftField,
      applyDraft,
      clearDraft,
      clearAll,
    ],
  );
};
